import { USD_TRY_RATES } from './constants';

// --- Currency conversion ---

/**
 * Convert TRY amount to USD using monthly exchange rate.
 * @param {number} tryValue - Amount in TRY
 * @param {string} monthKey - 'YYYY-MM' format
 * @returns {number} Amount in USD
 */
export function getUsdValue(tryValue, monthKey) {
  const rate = USD_TRY_RATES[monthKey];
  if (!rate) {
    // Fallback to most recent rate
    const keys = Object.keys(USD_TRY_RATES).sort();
    const fallbackRate = USD_TRY_RATES[keys[keys.length - 1]];
    return tryValue / fallbackRate;
  }
  return tryValue / rate;
}

// --- Number formatting (en-US locale) ---

/** Format as "1.2M", "450K", or plain number */
export function formatStat(value) {
  if (value == null || isNaN(value)) return '0';
  const num = Number(value);
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(num >= 10_000 ? 0 : 1) + 'K';
  return num.toLocaleString('en-US');
}

/** Format number with en-US locale grouping: "1,234" */
export function formatNumberCompact(value) {
  if (value == null || isNaN(value)) return '0';
  return Number(value).toLocaleString('en-US');
}

/** Format currency with compact notation: "$1.2M", "$450K", "$1,234" */
export function formatCurrencyCompact(value) {
  if (value == null || isNaN(value)) return '$0';
  const num = Number(value);
  if (num >= 1_000_000) return '$' + (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 10_000) return '$' + Math.round(num / 1_000) + 'K';
  return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Format currency without decimals: "$1,234" */
export function formatCurrencyUnit(value) {
  if (value == null || isNaN(value)) return '$0';
  return '$' + Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Format currency with 2 decimals: "$1,234.56" */
export function formatCurrencyUnitTwo(value) {
  if (value == null || isNaN(value)) return '$0.00';
  return '$' + Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// --- Date formatting ---

/** Format Date object to 'YYYY-MM-DD' in local timezone */
export function formatDateForAPI(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Alias for formatDateForAPI */
export const formatDateYMD = formatDateForAPI;

/** Add N days to a Date object (returns new Date) */
export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** Format a date string as short locale string: "Sep 8" */
export function formatLocalDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// --- Greeting ---

/** Returns time-of-day greeting in English */
export function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

// --- Percentage ---

/** Format a percentage change: "+12.5%" or "-3.2%" */
export function formatPercentChange(value) {
  if (value == null || isNaN(value)) return '0.0%';
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${Number(value).toFixed(1)}%`;
}
